import { useSelector } from 'react-redux';
import { useGetStartedEmail } from '../../../hooks/useUser';
import { trimLowerCase } from '../../../helpers/shared';
import { useTextLanguage } from '../../../hooks/useShared';
import {
  MainContainer,
  Content,
  Header,
  SubHeader,
  CardsWrapper,
  Card,
  CardImage,
  CardTitle,
  CardDesc,
  DescContainer,
  CardContent,
  OptionsContainer,
  ApplicationOptions,
  SlideViewport,
  SlideTrack,
  SlidePanel,
  StartingHeader,
  IconWrapper
} from './styled';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../theme/theme';
import { useAllowedCountries } from '../../../hooks/useRoutes';
import { AnimatePresence, motion } from 'motion/react'
import { useState, useEffect } from 'react';
import { LinkLogo } from '../LinkLogo';
import { checkMinutesPassed } from '../../../helpers/shared';
import { envSetting } from '../../../constants/envSetting';
import { LoaderSpinner } from '../../shared/simple/LoaderSpinner';
import { appsOrigin } from '../../../constants/appOrigin';
import { useInitUser } from '../../../hooks/useUser';
import { routes } from '../../../constants/routes';
import { IoChevronBackOutline } from "react-icons/io5";
import { useNavigate } from 'react-router-dom';
import SlideCaptchaModal from '../../shared/slide-captcha-modal/SlideCaptchaModal';
import { useOptInModal } from '../../../hooks/useOptInModal'; 
import { OptInModal } from './OptInModal/OptInModal';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material';
import RegisterForm from './RegisterForm';
import { RouterLink } from '../../shared/simple/RouterLink';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { USER_APP_ORIGIN_DATA } from '../../../redux/types/user';


const HomepageStarterEmail = () => {
  const { allowed } = useAllowedCountries();

  const { isOptInModalOpen, setIsOptInModalOpen } = useOptInModal()
  const {
    actualLanguage,
    appOrigin,
    loading,
    onNewRegister,
    isSlideModalOpen,
    setIsSlideModalOpen,
    values
  } = useInitUser(allowed, setIsOptInModalOpen);

  const {
    applications,
    errors,
    handleSubmit,
    onProduct,
    onSignUp,
    register,
    methods,
    isSignUpMailSent,
    switchMailSent,
    mailValues,
    productBackground,
    tick,
    setTick,
    isSending
  } = useGetStartedEmail();
  
  const { t } = useTranslation()
  const { selectedProduct, timeRefresh } = useSelector(state => state.user);
  /* State to handle the selected application */
  const [selectedApplications, setSelectedApplications] = useState(new Set());
  const showProductSelection = appOrigin === appsOrigin.default || !appOrigin;

  const responsiveTheme = useTheme();
  const isMobile = useMediaQuery(responsiveTheme.breakpoints.down('sm'));
  // ? Create variable to control whether or not to hide the product when they leave the view
  // ? to avoid error in mobile views
  const shouldHideProductSelection = isMobile && !showProductSelection

  return (
    <MainContainer
      productBg={productBackground}
    >
      <LinkLogo className="position-absolute top-0 left-0" />
      <Content shouldHideProductSelection={showProductSelection} small={true}>
        <SlideViewport>
          <SlideTrack
            animate={{ x: showProductSelection ? '0%' : '-50%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {/* Panel to show the products */}
            <SlidePanel>
              <Header>
                { t("getStarted.productServices") }
              </Header>
              <SubHeader>
                { t("getStarted.productServices2") }
              </SubHeader>

              <CardsWrapper>
                {(applications && !checkMinutesPassed(timeRefresh, envSetting.minutesRefreshApplication))
                  ? applications.map((product, i) => {
                    if (!product?.application?.active || !product?.application?.create_account) return null;
                    const title = product.application[`description_${trimLowerCase(actualLanguage)}`] || product.application.name;
                    const isSelected = selectedApplications.has(i);
                    if (shouldHideProductSelection) return
                    return (
                      <AplicationCard
                        onClick={ () => onProduct(product) }
                        product={product}
                        title={title}
                        key={`${title}-${i}`}
                        i={i}
                        isSelected={isSelected}
                        setSelectedApplications={setSelectedApplications}
                      />
                    )
                  })
                  : <LoaderSpinner />
                }
              </CardsWrapper>
            </SlidePanel>

            {/* Panel to show the form */}
            <SlidePanel style={{ display: 'flex', justifyContent: 'center' }}>
              <RegisterForm
                methods={methods}
                handleSubmit={handleSubmit}
                onNewRegister={onNewRegister}
                loading={loading}
                appOrigin={appOrigin}
                errors={errors}
                switchMailSent={switchMailSent}
                isSignUpMailSent={isSignUpMailSent}
                mailValues={mailValues}
                tick={tick}
                setTick={setTick}
                isSending={isSending}
              />
            </SlidePanel>
          </SlideTrack>
        </SlideViewport>
      </Content>
      <SlideCaptchaModal
        isOpen={ isSlideModalOpen }
        onRequestClose={ () => setIsSlideModalOpen(false) }
        onSuccess={ () => {
          onSignUp(values)
          // setIsOptInModalOpen(true)
        }}
      />
      <OptInModal 
        handleOpenIbv={() => console.log('handleOpenIbv')}
        modalIsOpen={ isOptInModalOpen }
        setModal={ setIsOptInModalOpen }
        onChangeStep={ () => { 
          setIsSlideModalOpen(true)
          setIsOptInModalOpen(false)
        }}
        appearance='new'
      />
    </MainContainer>
  )
}

const MotionCardContent = motion.create(CardContent)
const AplicationCard = ({ product, title, isSelected, setSelectedApplications, i, onClick }) => {
  const { languageText } = useTextLanguage()
  const { fifth } = theme;
  const [initial, setInitial] = useState(
    { opacity: 1, scale: 1 }
  );
  const { t } = useTranslation()
  // ? Utility state to handle the delay when unselecting a card, so it doesn't unselect immediately when the user accidentally leaves the card for a moment. 
  // ? It will only unselect after the given time, giving the user time to hover back on the card if they want to keep it selected.
  const [unselectingProduct, setUnselectingProduct] = useState(null);
  const dispatch = useDispatch()

  const onCardClick = () => {
    setSelectedApplications(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
    setInitial({ opacity: 0, scale: 0 });
  }

  const onMouseLeave = () => {
    if (!isSelected) return; // only schedule unselect for selected cards

    const timeoutId = setTimeout(() => {
      setSelectedApplications(prev => {
        const next = new Set(prev);
        next.delete(i);
        return next;
      });
    }, 1000);

    setUnselectingProduct(timeoutId);
  }

  const onMouseEnter = () => {
    if (unselectingProduct) {
      clearTimeout(unselectingProduct);
      setUnselectingProduct(null);
    }
  }

  const onNewRegisterClick = (e) => {
    e.stopPropagation()
    onClick()
  }

  const navigate = useNavigate()
  const onSeeRequest = () => {
    dispatch({
      type: USER_APP_ORIGIN_DATA,
      payload: product
    })
    if (product?.application?.unique_name === 'online_bank_account') {
      navigate(routes.login(appsOrigin.online_bank_account))
    } else {
      window.open(product?.app_route_fe ? product?.app_route_fe : routes.root, '_blank').focus();
    }
  }

  return (
    <Card 
        onClick={ onCardClick }
        onMouseLeave={ onMouseLeave }
        onMouseEnter={ onMouseEnter}
        fifth={fifth}
        isSelected={isSelected}
      >
      <AnimatePresence mode='wait'>
        {!isSelected ? (
          /* Front side of the card */
          <MotionCardContent
            initial={initial}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.25 }}
            key={ title }
          >
            <CardImage src={product.image_main} alt={product.application.unique_name} />
            <CardTitle>{title}</CardTitle>
            <DescContainer>
              <CardDesc
                title={product.application[`details${languageText}`] || ''}
              >
                {product.application[`details${languageText}`] || ''}
              </CardDesc>
            </DescContainer>
          </MotionCardContent>
        ) : (
          /* Back side of the card */
          <MotionCardContent
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.25 }}
            key={`${title}-selected`}
          >
            <OptionsContainer>
              <CardTitle>{title}</CardTitle>
              <ApplicationOptions 
                fifth={fifth}
              >
                <GoToLogin
                  to={routes.login(product?.description)}
                >
                  { t("login.buttonSignIn") }
                </GoToLogin>
              </ApplicationOptions>

              <ApplicationOptions 
                fifth={fifth}
                onClick={onNewRegisterClick}
              >
                { t("homepageGetStarted.newRegister") }
              </ApplicationOptions>

              <ApplicationOptions 
                fifth={fifth}
                onClick={onSeeRequest}
              >
                  { t("homepageGetStarted.seeRequests") }
              </ApplicationOptions>
            </OptionsContainer>
          </MotionCardContent>
        ) }
      </AnimatePresence>  
    </Card>
  )
}

export default HomepageStarterEmail

const GoToLogin = styled(Link)`
  width: 100%;
  height: 100%;
  color: black;
  text-decoration: none;
`;
